import React, { useState, useRef, useMemo } from 'react';
import { readFileAsText } from './helpers';
import Fuse from 'fuse.js';
import { DuplicateCheckModal, normalize as normalizeVendor } from './VendorPicker';


const STATUSES = [
  'In Database','Quote Requested','Quote Received','Presented to Client',
  'Sent to Austin','Proposal Received','Proposal Verified','Sent to Client',
  'Client Approved','Payment Received','PO Requested','PO Received',
  'PO Verified','Order Placed','Received'
];

const SCHEDULES = [
  'Furniture','Lighting','Art & Objects','Hardware','Appliances',
  'Tile & Stone','Paint & Wallpaper','Finish Schedule','Window Treatments','Softgoods','Millwork'
];

// Map common document terms to your schedule names
const SCHEDULE_MAP = {
  'furniture': 'Furniture',
  'sofa': 'Furniture', 'chair': 'Furniture', 'table': 'Furniture',
  'bed': 'Furniture', 'dresser': 'Furniture', 'cabinet': 'Furniture',
  'desk': 'Furniture', 'bench': 'Furniture', 'ottoman': 'Furniture',
  'lighting': 'Lighting', 'light': 'Lighting', 'lamp': 'Lighting',
  'chandelier': 'Lighting', 'sconce': 'Lighting', 'pendant': 'Lighting',
  'fixture': 'Lighting', 'lantern': 'Lighting', 'luminary': 'Lighting',
  'downlight': 'Lighting', 'uplight': 'Lighting', 'recessed': 'Lighting',
  'flush mount': 'Lighting', 'semi-flush': 'Lighting', 'floor lamp': 'Lighting',
  'table lamp': 'Lighting', 'task light': 'Lighting', 'reading light': 'Lighting',
  'wall light': 'Lighting', 'ceiling light': 'Lighting', 'spot light': 'Lighting',
  'track light': 'Lighting', 'strip light': 'Lighting', 'led strip': 'Lighting',
  'art': 'Art & Objects', 'mirror': 'Art & Objects', 'sculpture': 'Art & Objects',
  'object': 'Art & Objects', 'vase': 'Art & Objects', 'accessory': 'Art & Objects',
  'hardware': 'Hardware', 'knob': 'Hardware', 'pull': 'Hardware',
  'handle': 'Hardware', 'hinge': 'Hardware', 'hook': 'Hardware',
  'appliance': 'Appliances', 'refrigerator': 'Appliances', 'dishwasher': 'Appliances',
  'oven': 'Appliances', 'range': 'Appliances', 'hood': 'Appliances',
  'tile': 'Tile & Stone', 'stone': 'Tile & Stone', 'marble': 'Tile & Stone',
  'slate': 'Tile & Stone', 'porcelain': 'Tile & Stone', 'mosaic': 'Tile & Stone',
  'paint': 'Paint & Wallpaper', 'wallpaper': 'Paint & Wallpaper',
  'wallcovering': 'Paint & Wallpaper', 'fabric': 'Paint & Wallpaper',
  'finish': 'Finish Schedule', 'floor': 'Finish Schedule', 'flooring': 'Finish Schedule',
  'carpet': 'Finish Schedule', 'wood floor': 'Finish Schedule',
  'bedding': 'Softgoods', 'duvet': 'Softgoods', 'coverlet': 'Softgoods',
  'quilt': 'Softgoods', 'sham': 'Softgoods', 'towel': 'Softgoods',
  'washcloth': 'Softgoods', 'linens': 'Softgoods', 'bath mat': 'Softgoods',
  'curtain': 'Window Treatments', 'drapery': 'Window Treatments',
  'blind': 'Window Treatments', 'shade': 'Window Treatments',
  'drape': 'Window Treatments', 'valance': 'Window Treatments',
  'millwork': 'Millwork', 'cabinetry': 'Millwork', 'built-in': 'Millwork',
  'shelving': 'Millwork', 'paneling': 'Millwork', 'trim': 'Millwork',
  'rug': 'Furniture', 'carpet runner': 'Furniture',
  'plumbing': 'Hardware', 'faucet': 'Hardware', 'sink': 'Hardware',
  'toilet': 'Hardware', 'shower': 'Hardware', 'tub': 'Hardware',
};

function mapToSchedule(itemName, categoryFromDoc) {
  if (!itemName && !categoryFromDoc) return 'Furniture';
  const text = ((itemName || '') + ' ' + (categoryFromDoc || '')).toLowerCase();
  for (const [key, schedule] of Object.entries(SCHEDULE_MAP)) {
    if (text.includes(key)) return schedule;
  }
  // Try direct match on category
  if (categoryFromDoc) {
    const match = SCHEDULES.find(s => s.toLowerCase() === categoryFromDoc.toLowerCase());
    if (match) return match;
  }
  return 'Furniture'; // safe default
}

const DOC_TYPES = [
  { value: 'schedule', label: 'FF&E Schedule', desc: 'Master item list with rooms, specs, quantities' },
  { value: 'vendors', label: 'Vendor List', desc: 'List of vendors with contact info' },
  { value: 'quote', label: 'Vendor Quote', desc: 'Pricing from a vendor for specific items' },
  { value: 'proposal', label: 'Proposal from Austin', desc: 'Client proposal with pricing' },
  { value: 'po', label: 'Purchase Order', desc: 'PO issued to a vendor' },
  { value: 'other', label: 'Other Document', desc: 'Let Claude figure it out' },
];

const inp = {
  padding: '5px 7px', border: '1px solid #ddd', borderRadius: 4,
  fontSize: 11, fontFamily: 'inherit', outline: 'none',
  background: '#fff', width: '100%', boxSizing: 'border-box',
};

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

async function readExcelAsText(file) {
  // Load SheetJS from CDN
  if (!window.XLSX) {
    await new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
      s.onload = resolve; s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  const buf = await readFileAsArrayBuffer(file);
  const wb = window.XLSX.read(buf, { type: 'array' });
  // Convert all sheets to CSV text
  const csvParts = wb.SheetNames.map(name => {
    const ws = wb.Sheets[name];
    return '=== Sheet: ' + name + ' ===\n' + window.XLSX.utils.sheet_to_csv(ws);
  });
  return csvParts.join('\n\n');

}

const STATUS_MAP = {
  'ordered': 'Ordered','paid, in progress': 'Payment Received',
  'completed ready for install': 'At Receiver','completed - ready for install': 'At Receiver',
  'in storage': 'At Receiver','proposal approved by client': 'Client Approved',
  'proposal ready for review': 'Proposal Received','specifying': 'Specifying',
  'quoting': 'Quote Received','next proposal': 'Quote Received',
  'not ordered': 'Presented to Client','tbd': 'In Database',
  'total cost': 'In Database','reworking design': 'In Database','n/a': 'In Database',
};

function mapStatus(designing, procurement) {
  const combined = [designing, procurement].map(s => (s || '').toLowerCase().trim());
  for (const val of combined) {
    for (const [key, mapped] of Object.entries(STATUS_MAP)) {
      if (val.includes(key)) return mapped;
    }
  }
  return 'In Database';
}


function parsePrice(str) {
  if (!str) return 0;
  const val = parseFloat(str.replace(/[$,\s]/g, '').replace('#REF!', '0'));
  return isNaN(val) ? 0 : val;
}


function parseCSV(text) {
  return text.split('\n').map(line => {
    const cols = []; let inQ = false, cur = '';
    for (const ch of line) {
      if (ch === '"') { inQ = !inQ; }
      else if (ch === ',' && !inQ) { cols.push(cur.trim()); cur = ''; }
      else cur += ch;
    }
    cols.push(cur.trim()); return cols;
  });
}


function processScheduleRows(rows, projectLabel) {
  let hi = -1;
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].includes('ITEM') && rows[i].includes('LOCATION')) { hi = i; break; }
  }
  if (hi === -1) return [];
  const H = rows[hi].map(h => h.toUpperCase().trim());
  const c = n => H.indexOf(n);
  const C = {
    item:c('ITEM'), location:c('LOCATION'), tag:c('TAG'),
    qty: c('QTY') >= 0 ? c('QTY') : c('QUANTITY'),
    vendor:c('VENDOR'), manufacturer:c('MANUFACTURER'), description:c('DESCRIPTION'),
    model:c('MODEL'), size:c('SIZE'), finish:c('FINISH'),
    cost:c('COST'), total:c('TOTAL'),
    designing:c('DESIGNING'), procurement:c('PROCUREMENT'),
    notes:c('INTERNAL NOTES'), itemNotes:c('NOTES'),
    leadTime:c('LEAD TIME'), contact:c('CONTACT'),
    link:c('LINK'),
    technology:c('TECHNOLOGY'), bulb:c('BULB'), base:c('BASE'),
    wattage:c('WATTAGE'), lumens:c('LUMENS'), kelvins:c('KELVINS'),
    cri:c('CRI'), voltage:c('VOLTAGE'), circuitry:c('CIRCUITRY'),
    tariff:c('TARIFF'), freight:c('FREIGHT'),
  };
  const items = [];
  for (let i = hi + 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.length < 5 || !row[C.item]) continue;
    const loc = row[C.location] || '';
    const lm = loc.match(/^(\d+)\s+(.+)$/);
    const tag = row[C.tag] || '';
    items.push({
      id: `import-${Date.now()}-${i}`, isChild: tag === '^', parentId: null,
      importSource: '',
      importDate: new Date().toLocaleDateString(),
      roomName: (lm ? lm[2] : loc).trim(), roomNumber: (lm ? lm[1] : '').trim(),
      item: row[C.item].trim(), productName: C.link>=0?(row[C.link]||'').trim():'', category: 'Furniture',
      vendor: (row[C.vendor]||'').trim(), qty: String(parseFloat(row[C.qty]||'')||1),
      qtyType: 'Each', status: mapStatus(row[C.designing], row[C.procurement]),
      netPrice: parsePrice(row[C.cost]), clientPrice: parsePrice(row[C.total]),
      projectLabel: projectLabel || '', notes: [row[C.notes],row[C.itemNotes]].filter(Boolean).join(' | '),
      specs: { manufacturer:(row[C.manufacturer]||'').trim(), model:(row[C.model]||'').trim(),
        dimensions:(row[C.size]||'').trim(), finish:(row[C.finish]||'').trim(),
        description:(row[C.description]||'').trim(), leadTime:(row[C.leadTime]||'').trim(),
        tag: tag !== '^' ? tag : '',
        // Lighting specs — populated if present, empty string if not
        technology: C.technology>=0?(row[C.technology]||'').trim():'',
        bulb: C.bulb>=0?(row[C.bulb]||'').trim():'',
        base: C.base>=0?(row[C.base]||'').trim():'',
        wattage: C.wattage>=0?(row[C.wattage]||'').trim():'',
        lumens: C.lumens>=0?(row[C.lumens]||'').trim():'',
        kelvins: C.kelvins>=0?(row[C.kelvins]||'').trim():'',
        cri: C.cri>=0?(row[C.cri]||'').trim():'',
        voltage: C.voltage>=0?(row[C.voltage]||'').trim():'',
        circuitry: C.circuitry>=0?(row[C.circuitry]||'').trim():'',
      },
      com:{}, childType:'', children:[],
      fees: {
        tariff: C.tariff>=0?((row[C.tariff]||'').replace(/[$,]/g,'')||''):'',
        freight: C.freight>=0?((row[C.freight]||'').replace(/[$,]/g,'')||''):'',
        salesTax:'', crating:'', custom:[],
      },
    });
  }
  let lpi = -1;
  for (let i = 0; i < items.length; i++) {
    if (!items[i].isChild) { lpi = i; }
    else if (lpi >= 0) {
      items[i].parentId = items[lpi].id;
      const n = items[i].item.toUpperCase();
      items[i].childType = (n.includes('COM')||n.includes('COL')||n.includes('FABRIC')||n.includes('LEATHER'))
        ? 'COM/COL Fabric' : n.includes('TRIM')||n.includes('FRINGE') ? 'Trim / Passementerie' : 'COM/COL Fabric';
    }
  }
  return items;
}


async function parseWithClaude(fileContent, fileName, docType, projectLabel, isBase64, mimeType) {
  const sys = `You are a data extraction specialist for Cimon Starr Interior Design, a high-end interior design firm in San Francisco.

Your job is to extract individual procurement items from design documents and return structured JSON.

Return ONLY a JSON array of items — no explanation, no markdown, just the array:
[
  {
    "item": "item name/description",
    "category": "one of: Furniture, Lighting, Art & Objects, Hardware, Appliances, Tile & Stone, Paint & Wallpaper, Finish Schedule, Window Treatments, Millwork",
    "projectArea": "for Hoback project: Main Living, Lock Out, Family Bedrooms, or Primary Suite if mentioned; otherwise empty string",
    "vendor": "vendor or supplier name",
    "manufacturer": "manufacturer/brand if different from vendor",
    "model": "model number or SKU",
    "roomName": "room name",
    "roomNumber": "room number if present",
    "qty": "quantity as a number string",
    "qtyType": "Each, Pair, Set, LF, SF, etc",
    "dimensions": "dimensions as given",
    "finish": "finish, color, or material",
    "description": "full product description",
    "netCost": "net/trade cost as number string, no $ sign",
    "clientPrice": "client price if shown, no $ sign",
    "status": "best matching status from: In Database, Quote Requested, Quote Received, Presented to Client, Sent to Austin, Proposal Received, Proposal Verified, Sent to Client, Client Approved, Payment Received, PO Requested, PO Received, PO Verified, Order Placed, Received",
    "leadTime": "lead time if mentioned",
    "notes": "any other relevant notes",
    "confidence": "high, medium, or low"
  }
]

Rules:
- Extract EVERY item you can find — don't skip partial entries
- If the document is very large, extract as many items as possible within the response limit — prioritize completeness of each item over total item count
- Always close the JSON array properly with ] even if you run out of space
- For Hoback project: it has four areas — Main Living, Lock Out, Family Bedrooms, Primary Suite (also called Master Suite). Use these as projectArea if mentioned in the document
- Common Hoback room numbers: 101-199 typically. Room names often include descriptors like "Living Room", "Dining", "Kitchen", "Master Bedroom", "Guest Room" etc
- If the document title or header mentions an area name, apply it to all items in that document
- If a field is not present, use empty string ""
- For status: if document is a PO, status is at least "PO Received". If proposal, at least "Proposal Received". If schedule with ordered items, "Order Placed". Otherwise "In Database"
- For category: make your best guess from the item name
- Never invent data — only extract what's actually in the document
- If the document mentions a project name like "Hoback" or "Ptarmigan", note it but use the project passed in
- COM/fabric items attached to furniture: include as separate items with item name like "COM - [fabric name]"`;

  const userMsg = docType === 'other'
    ? `File: ${fileName}\nProject: ${projectLabel}\n\nPlease identify what type of document this is and extract all items you can find.`
    : `File: ${fileName}\nDocument type: ${docType}\nProject: ${projectLabel}\n\nExtract all items from this document.`;

  let messageContent;
  if (isBase64 && mimeType?.includes('pdf')) {
    messageContent = [
      { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: fileContent } },
      { type: 'text', text: userMsg }
    ];
  } else if (isBase64 && mimeType?.startsWith('image/')) {
    messageContent = [
      { type: 'image', source: { type: 'base64', media_type: mimeType, data: fileContent } },
      { type: 'text', text: userMsg }
    ];
  } else {
    messageContent = `${userMsg}\n\nDocument content:\n${fileContent.slice(0, 20000)}`;
  }

  const res = await fetch('/api/claude',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({system:sys,user:messageContent,max_tokens:8000})});

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error('API error ' + res.status + ': ' + (errData.error?.message || res.statusText));
  }
  const data = await res.json();
  if (data.error) throw new Error(data.error.message || 'Claude API error');
  const text = data.text || '';
  if (!text) throw new Error('Claude returned empty response');
  // Try to extract JSON array from response
  const cleaned = text.replace(/```json|```/g, '').trim();
  // Find the JSON array even if there's extra text
  const arrayStart = cleaned.indexOf('[');
  if (arrayStart === -1) {
    console.error('No JSON array found:', cleaned.slice(0, 300));
    throw new Error('Claude could not find items in this document. Try a different file format or check that the document contains a list of items.');
  }
  let arrayEnd = cleaned.lastIndexOf(']');
  let jsonStr = arrayEnd > arrayStart ? cleaned.slice(arrayStart, arrayEnd + 1) : cleaned.slice(arrayStart);
  // Repair truncated JSON - if response was cut off, close open objects
  try {
    return JSON.parse(jsonStr);
  } catch (parseErr) {
    // Try to repair truncated JSON
    console.warn('JSON parse failed, attempting repair:', parseErr.message);
    // Remove the last incomplete item (everything after the last complete },)
    const lastCompleteItem = jsonStr.lastIndexOf('},');
    const lastCompleteItem2 = jsonStr.lastIndexOf('}\n');
    const cutPoint = Math.max(lastCompleteItem, lastCompleteItem2);
    if (cutPoint > arrayStart) {
      const repaired = jsonStr.slice(0, cutPoint + 1) + ']';
      try {
        const result = JSON.parse(repaired);
        console.warn('Repaired truncated JSON, recovered ' + result.length + ' items');
        return result;
      } catch (repairErr) {
        // Last resort - try to find any valid items
        const itemMatches = jsonStr.match(/\{[^{}]+\}/g) || [];
        if (itemMatches.length > 0) {
          const items = itemMatches.map(m => { try { return JSON.parse(m); } catch { return null; } }).filter(Boolean);
          if (items.length > 0) return items;
        }
        throw new Error('Document parsed but response was cut off. The file may be too large — try splitting it into smaller sections (e.g. one room at a time) or export just the first 50 rows as a test.');
      }
    }
    throw new Error('Document parsed but response was cut off. Try splitting the file into smaller sections.');
  }
}

function processVendorRows(rows) {
  // Find header row — look for a row containing 'Vendor', 'Company', or 'Name'
  // Handles leading blank rows and rows starting with ':' (like Cimon's vendor CSV)
  let hi = -1;
  for (let i = 0; i < Math.min(rows.length, 15); i++) {
    const joined = rows[i].join(' ').toUpperCase();
    if (joined.includes('VENDOR') || joined.includes('COMPANY') || joined.includes('NAME')) {
      const nonEmpty = rows[i].filter(c => c.trim()).length;
      if (nonEmpty >= 2) { hi = i; break; }
    }
  }
  if (hi === -1) return [];

  // Strip leading ':' from header cells (artifact from some exports)
  const H = rows[hi].map(h => h.replace(/^:/, '').toUpperCase().trim());

  function col(...names) {
    for (const n of names) {
      const i = H.findIndex(h => h.includes(n));
      if (i >= 0) return i;
    }
    return -1;
  }

  const C = {
    name:     col('VENDOR', 'NAME', 'COMPANY', 'FIRM'),
    location: col('LOCATION', 'CITY', 'OFFICE'),
    contact:  col('CONTACT', 'REP', 'SALES', 'PERSON'),
    category: col('CATEGORY', 'TYPE', 'SPECIALTY'),
    phone:    col('PHONE', 'TEL', 'MOBILE', 'CELL'),
    email:    col('EMAIL', 'E-MAIL', 'EMAILL'),  // handles common typo 'Emaill'
    notes:    col('NOTES', 'NOTE'),
    address:  col('ADDRESS', 'STREET', 'ADDR'),
    state:    col('STATE'),
    zip:      col('ZIP', 'POSTAL'),
  };

  const seen = new Set();
  const vendors = [];

  for (let i = hi + 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.length < 2) continue;

    const name = C.name >= 0 ? (row[C.name] || '').trim() : '';
    if (!name) continue;

    // Skip duplicates by name
    if (seen.has(name.toLowerCase())) continue;
    seen.add(name.toLowerCase());

    // Merge category + notes into notes field
    const category = C.category >= 0 ? (row[C.category] || '').trim() : '';
    const rawNotes = C.notes   >= 0 ? (row[C.notes]    || '').trim() : '';
    const notes = [category, rawNotes].filter(Boolean).join(' · ');

    // Location = showroom city (skip 'N/A')
    const location = C.location >= 0 ? (row[C.location] || '').trim() : '';
    const city = (location === 'N/A' || location === 'n/a') ? '' : location;

    vendors.push({
      id: Date.now() + i + Math.random(),
      name,
      contact:  C.contact >= 0 ? (row[C.contact] || '').trim() : '',
      email:    C.email   >= 0 ? (row[C.email]   || '').trim() : '',
      phone:    C.phone   >= 0 ? (row[C.phone]   || '').trim() : '',
      website:  '',
      notes,
      address:  C.address >= 0 ? (row[C.address] || '').trim() : '',
      address2: '',
      city,
      state:    C.state   >= 0 ? (row[C.state]   || '').trim() : '',
      zip:      C.zip     >= 0 ? (row[C.zip]     || '').trim() : '',
      shipAttn: '',
    });
  }
  return vendors;
}

export default function ImportWizard({ projects, onImport, existingItems, onImportVendors, vendors, onAddVendor }) {
  const [step, setStep] = useState(1); // 1=setup, 2=parsing, 3=review
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState('');
  const [projectLabel, setProjectLabel] = useState('');
  const [isPreApp, setIsPreApp] = useState(false);
  const [forceSchedule, setForceSchedule] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parseStage, setParseStage] = useState('');
  const [items, setItems] = useState([]);
  const [editingIdx, setEditingIdx] = useState(null);
  const [selected, setSelected] = useState({});
  const [done, setDone] = useState(false);
  const [duplicates, setDuplicates] = useState([]); // [{incoming, existing}]
  const [dupStep, setDupStep] = useState(false); // show dup review screen
  const [dragOver, setDragOver] = useState(false);
  const [vendorPreview, setVendorPreview] = useState(null); // parsed vendors awaiting confirm
  // Vendor resolution gate — between Review and onImport. The user MUST
  // resolve every distinct unrecognized vendor name before items can be
  // committed: pick an existing canonical name, add as new (via shared
  // DuplicateCheckModal), or skip those rows entirely. Stops bulk import
  // from silently spawning vendor-name drift in QuickBooks.
  const [vendorResolveStep, setVendorResolveStep] = useState(false);
  // { [originalName]: { action: 'matched'|'new'|'skip'|'blank', resolvedName?: string } }
  const [vendorResolutions, setVendorResolutions] = useState({});
  const [addNewForVendor, setAddNewForVendor] = useState(null); // original name being added
  const fileRef = useRef(null);

  function handleFile(f) {
    if (!f) return;
    setFile(f);
    setVendorPreview(null);
    // Auto-detect doc type from filename
    const name = f.name.toLowerCase();
    if (name.includes('vendor') || name.includes('contact') || name.includes('login')) setDocType('vendors');
    else if (name.includes('schedule') || name.includes('ffe') || name.includes('ff&e') || f.name.endsWith('.csv')) setDocType('schedule');
    else if (name.includes('po') || name.includes('purchase')) setDocType('po');
    else if (name.includes('proposal')) setDocType('proposal');
    else if (name.includes('quote')) setDocType('quote');
    else setDocType('other');
  }

  const startParsing = async () => {
    if (!file || !docType) return;
    // Vendors don't need a project — everything else does
    if (docType !== 'vendors' && !projectLabel) return;
    setParsing(true); setStep(2);
    setParseStage('Reading file...');

    try {
      let fileContent = '';
      let isBase64 = false;
      const mimeType = file.type;
      const isCSV = file.name.toLowerCase().endsWith('.csv') || mimeType.includes('csv') || mimeType === 'text/plain';
      const isPDF = mimeType === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      const isImage = mimeType.startsWith('image/');
      const isExcel = /\.(xlsx|xls|xlsm)$/i.test(file.name) || mimeType.includes('spreadsheet') || mimeType.includes('excel');

      let extracted = [];

      // ── VENDOR LIST ──────────────────────────────────────────────────────
      if (docType === 'vendors') {
        setParseStage('Reading vendor list...');
        fileContent = await readFileAsText(file);
        const rows = parseCSV(fileContent);
        const vendors = processVendorRows(rows);
        if (vendors.length === 0) {
          setParseStage('No vendors found — check column headers include Name, Email, Phone, etc.');
          setParsing(false); setStep(1);
          return;
        }
        setVendorPreview(vendors);
        setParsing(false); setStep(3);
        return;
      }

      if (isCSV && docType === 'schedule') {
        // Use direct parser for Google Sheets CSV schedules — fast and reliable
        setParseStage('Reading CSV schedule...');
        fileContent = await readFileAsText(file);
        const rows = parseCSV(fileContent);
        const rawItems = processScheduleRows(rows, projectLabel);
        // Convert to ImportWizard format
        extracted = rawItems.filter(it => !it.isChild).map(it => ({
          item: it.item || '',
          category: forceSchedule || it.category || 'Furniture',
          vendor: it.vendor || '',
          productName: it.productName || '',
          manufacturer: it.specs?.manufacturer || '',
          model: it.specs?.model || '',
          roomName: it.roomName || '',
          roomNumber: it.roomNumber || '',
        qty: it.qty || '1',
          qtyType: it.qtyType || 'Each',
          dimensions: it.specs?.dimensions || '',
          finish: it.specs?.finish || '',
          description: it.specs?.description || '',
          netPrice: it.netPrice != null ? String(it.netPrice) : '',
          clientPrice: it.clientPrice != null ? String(it.clientPrice) : '',
          total: it.clientPrice != null ? String(it.clientPrice) : '',
          status: it.status || 'In Database',
          leadTime: it.specs?.leadTime || '',
          notes: it.notes || '',
          importSource: isPreApp ? 'ABD' : '',
          confidence: 'high',
          _children: rawItems.filter(c => c.isChild && c.parentId === it.id),
          // Pass entire specs object so lighting/other fields aren't lost
          _rawSpecs: it.specs || {},
          _rawFees: it.fees || {},
        }));
        if (extracted.length === 0) {
          throw new Error('No items found in CSV. Make sure it has ITEM and LOCATION columns (standard Google Sheets schedule format).');
        }

      } else if (isPDF || isImage) {
        fileContent = await readFileAsBase64(file);
        isBase64 = true;
        setParseStage('Claude is reading the document...');
        extracted = await parseWithClaude(fileContent, file.name, docType, projectLabel, isBase64, mimeType);
      } else if (isExcel) {
        setParseStage('Reading Excel file...');
        try {
          fileContent = await readExcelAsText(file);
        } catch (xlsxErr) {
          throw new Error('Could not read Excel file: ' + xlsxErr.message + '. Try saving as CSV from Google Sheets first.');
        }
        setParseStage('Claude is reading the spreadsheet...');
        extracted = await parseWithClaude(fileContent, file.name, docType, projectLabel, false, mimeType);
      } else {
        fileContent = await readFileAsText(file);
        setParseStage('Claude is reading the document...');
        extracted = await parseWithClaude(fileContent, file.name, docType, projectLabel, false, mimeType);
      }

      setParseStage('Building preview...');
      const stamped = extracted.map((it, i) => {
        const schedule = forceSchedule || mapToSchedule(it.item, it.category);
        return {
        ...it,
        _id: `import-${Date.now()}-${i}`,
        project: projectLabel,
        projectLabel,
        importSource: isPreApp ? 'ABD' : '',
        importDate: new Date().toLocaleDateString(),
        status: it.status || 'In Database',
        category: schedule,
        schedule: schedule,
        qty: it.qty || '1',
        qtyType: it.qtyType || 'Each',
        specs: Object.assign({
          manufacturer: it.manufacturer || '',
          model: it.model || '',
          dimensions: it.dimensions || '',
          finish: it.finish || '',
          description: it.description || '',
        }, it._rawSpecs || {}),
        netPrice: it.netPrice || it.netPrice || '',
        clientPrice: it.clientPrice || it.total || '',
        total: it.total || it.clientPrice || '',
        leadTime: it.leadTime || '',
        notes: it.notes || '',
        projectArea: it.projectArea || '',
        _selected: true,
      }; });

      const sel = {};
      stamped.forEach((_, i) => { sel[i] = true; });
      setSelected(sel);
      setItems(stamped);
      setStep(3);
    } catch (e) {
      console.error('Parse error:', e);
      const msg = e.message || 'Unknown error';
      // Show error in UI rather than alert, go to error step
      setParseStage('');
      setItems([{ 
        _id: 'error', item: '', _error: true,
        errorMessage: msg,
      }]);
      setStep(3); // show review step with error
    }
    setParsing(false);
  }

  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  }

  const updateSpec = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, specs: { ...it.specs, [field]: value } } : it));
  }

  const toggleSelect = (idx) => {
    setSelected(prev => ({ ...prev, [idx]: !prev[idx] }));
  }

  const selectAll = (val) => {
    const s = {};
    items.forEach((_, i) => { s[i] = val; });
    setSelected(s);
  }

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
    setSelected(prev => {
      const n = {};
      Object.keys(prev).forEach(k => { if (parseInt(k) < idx) n[k] = prev[k]; else if (parseInt(k) > idx) n[parseInt(k)-1] = prev[k]; });
      return n;
    });
  }

  // Distinct vendor names across SELECTED items, with auto-match against
  // the directory. Drives the resolution gate.
  const distinctVendorRows = useMemo(() => {
    const vendorList = vendors || [];
    const nameToCanonical = {};
    vendorList.forEach(v => { if (v && v.name) nameToCanonical[v.name.toLowerCase().trim()] = v.name; });
    const counts = {};
    items.forEach((it, idx) => {
      if (selected[idx] === false) return;
      const raw = (it.vendor || '').trim();
      if (!raw) return; // blank vendor — not a resolution concern
      const key = raw; // preserve case for display; match case-insensitively below
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.keys(counts).map(origName => {
      const exact = nameToCanonical[origName.toLowerCase().trim()];
      return { origName, count: counts[origName], exactMatch: exact || null };
    }).sort((a, b) => b.count - a.count);
  }, [items, selected, vendors]);

  // Fuse instance for fuzzy match against the vendor directory. Keys on
  // both raw name and the normalized form so "Casa Design" surfaces
  // "Casa Design, LLC" the same way VendorPicker does.
  const vendorFuse = useMemo(() => {
    const vendorList = (vendors || []).map(v => Object.assign({}, v, { _normalized: normalizeVendor(v.name) }));
    return new Fuse(vendorList, {
      keys: ['name', '_normalized'],
      threshold: 0.4,
      includeScore: true,
      ignoreLocation: true,
      minMatchCharLength: 2,
    });
  }, [vendors]);

  function fuzzyMatchesFor(origName) {
    const q = (origName || '').trim();
    if (q.length < 2) return [];
    const norm = normalizeVendor(q);
    const raw = vendorFuse.search(q).map(r => ({ v: r.item, score: r.score }));
    const byNorm = vendorFuse.search(norm).map(r => ({ v: r.item, score: r.score }));
    const merged = {};
    raw.concat(byNorm).forEach(m => {
      const prev = merged[m.v.id];
      if (!prev || m.score < prev.score) merged[m.v.id] = m;
    });
    return Object.values(merged).sort((a, b) => a.score - b.score).slice(0, 3).map(m => m.v);
  }

  // True when every distinct vendor name has been resolved (matched / new / skip / blank).
  const allVendorsResolved = useMemo(() => {
    return distinctVendorRows.every(r => {
      if (r.exactMatch) return true; // auto-pass — already in directory
      const res = vendorResolutions[r.origName];
      return !!(res && res.action);
    });
  }, [distinctVendorRows, vendorResolutions]);

  // The single source of truth: builds the actual records to import,
  // applying vendor resolutions and filtering out items whose vendor
  // was 'skip'-resolved. Called by commitImport once the resolution
  // gate has passed.
  function buildResolvedImportSet() {
    const selectedItems = items.filter((_, i) => selected[i] !== false);
    const resolved = [];
    selectedItems.forEach(it => {
      const raw = (it.vendor || '').trim();
      if (raw) {
        const vendorList = vendors || [];
        const exact = vendorList.find(v => v && v.name && v.name.toLowerCase().trim() === raw.toLowerCase().trim());
        if (exact) {
          // Canonicalize case to match directory entry exactly.
          resolved.push({ ...it, vendor: exact.name });
          return;
        }
        const res = vendorResolutions[raw];
        if (!res || !res.action) return; // shouldn't happen — gate enforces
        if (res.action === 'skip') return; // item stays in 'needs vendor' state — not imported
        if (res.action === 'matched') { resolved.push({ ...it, vendor: res.resolvedName }); return; }
        if (res.action === 'new')     { resolved.push({ ...it, vendor: res.resolvedName }); return; }
        if (res.action === 'blank')   { resolved.push({ ...it, vendor: '' }); return; }
      } else {
        resolved.push(it);
      }
    });
    return resolved.map(it => ({
      ...it,
      id: it._id,
      _id: undefined,
      _selected: undefined,
      children: [],
      parentId: null,
      imageData: '',
      imageUrl: '',
      images: [],
      heroIndex: 0,
      date: new Date().toLocaleDateString(),
    }));
  }

  function runDuplicateCheckAndCommit(toImport) {
    // Check for duplicates by item name (case-insensitive)
    const existing = existingItems || [];
    const dups = [];
    const clean = [];

    toImport.forEach(it => {
      const match = existing.find(e =>
        e.item && it.item &&
        e.item.toLowerCase().trim() === it.item.toLowerCase().trim()
      );
      if (match) {
        dups.push({ incoming: it, existing: match });
      } else {
        clean.push(it);
      }
    });

    if (dups.length > 0) {
      // Show duplicate review screen
      setDuplicates(dups.map(d => ({ ...d, choice: 'skip' }))); // default skip
      setDupStep(true);
      // Still import non-duplicates immediately
      if (clean.length > 0) onImport(clean);
    } else {
      onImport(toImport);
      setDone(true);
    }
  }

  const commitImport = () => {
    // Vendor resolution gate. Any distinct vendor name not already in
    // the directory must be resolved before items can flow through to
    // onImport. Auto-matched names (exact directory hit) pass through;
    // anything else routes the user through fuzzy-match resolution UI.
    const needsResolution = distinctVendorRows.some(r => !r.exactMatch && !vendorResolutions[r.origName]);
    if (needsResolution) {
      setVendorResolveStep(true);
      return;
    }
    runDuplicateCheckAndCommit(buildResolvedImportSet());
  }

  const commitDuplicates = () => {
    const toUpdate = duplicates.filter(d => d.choice === 'replace').map(d => ({
      ...d.incoming,
      id: d.existing.id, // use existing ID so it updates not creates
    }));
    const toAdd = duplicates.filter(d => d.choice === 'add').map(d => d.incoming);
    if (toUpdate.length > 0 || toAdd.length > 0) {
      onImport([...toUpdate, ...toAdd]);
    }
    setDone(true);
    setDupStep(false);
  }

  const reset = () => {
    setStep(1); setFile(null); setDocType(''); setProjectLabel(''); setForceSchedule('');
    setItems([]); setSelected({}); setDone(false); setEditingIdx(null);
    setVendorPreview(null); setDuplicates([]); setDupStep(false);
    setVendorResolveStep(false); setVendorResolutions({}); setAddNewForVendor(null);
  }

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const totalCount = items.length;

  // ── VENDOR RESOLUTION ───────────────────────────────────────────────────
  // For every distinct vendor name on the selected import rows, the user
  // resolves to an existing directory entry, adds as a new vendor (via
  // the shared DuplicateCheckModal), skips those rows entirely, or
  // imports them with a blank vendor. Nothing flows through to
  // duplicate-check / onImport until every row's vendor name has a
  // resolution.
  if (vendorResolveStep) {
    const unresolvedCount = distinctVendorRows.filter(r => !r.exactMatch && !vendorResolutions[r.origName]).length;
    const skippedItemCount = distinctVendorRows.reduce((acc, r) => {
      const res = vendorResolutions[r.origName];
      return res && res.action === 'skip' ? acc + r.count : acc;
    }, 0);
    return (
      <div style={{ maxWidth: 920 }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
            Resolve vendors before importing
          </div>
          <div style={{ fontSize: 13, color: '#666', lineHeight: 1.5 }}>
            Each unrecognized vendor name below must be matched to an existing vendor, added as a new vendor, or skipped. Items whose vendor is left unresolved will not be imported.
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
          {distinctVendorRows.map((row, i) => {
            const res = vendorResolutions[row.origName];
            const auto = !!row.exactMatch;
            const matches = auto ? [] : fuzzyMatchesFor(row.origName);
            const status = auto
              ? { label: 'Matched directory', bg: '#E8F5E9', color: '#1A5C38' }
              : res && res.action === 'matched' ? { label: 'Matched to ' + res.resolvedName, bg: '#E8F5E9', color: '#1A5C38' }
              : res && res.action === 'new'     ? { label: 'Add as new vendor: ' + res.resolvedName, bg: '#E8F5E9', color: '#1A5C38' }
              : res && res.action === 'blank'   ? { label: 'Import with blank vendor', bg: '#FFF8E1', color: '#BF6800' }
              : res && res.action === 'skip'    ? { label: 'Skip these ' + row.count + ' item' + (row.count === 1 ? '' : 's'), bg: '#FFEBEE', color: '#C62828' }
              : { label: 'Needs resolution', bg: '#FFF8E1', color: '#BF6800' };

            return (
              <div key={i} style={{ border: '1px solid #E0E0E0', borderRadius: 8, padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{row.origName || <em style={{ color: '#888' }}>(blank)</em>}</div>
                    <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{row.count} item{row.count === 1 ? '' : 's'} from this import</div>
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 10, background: status.bg, color: status.color }}>
                    {status.label}
                  </div>
                </div>

                {auto && (
                  <div style={{ fontSize: 12, color: '#1A5C38' }}>
                    Already in your vendor directory — items will be imported under this exact name.
                  </div>
                )}

                {!auto && (
                  <div>
                    {matches.length > 0 && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#888', letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 6 }}>
                          Closest matches in directory
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {matches.map(v => {
                            const isPicked = res && res.action === 'matched' && res.resolvedName === v.name;
                            return (
                              <button key={v.id}
                                onClick={() => setVendorResolutions(prev => ({ ...prev, [row.origName]: { action: 'matched', resolvedName: v.name } }))}
                                style={{
                                  padding: '6px 12px', fontSize: 12, fontWeight: 600,
                                  border: '1px solid', borderColor: isPicked ? '#1a1a1a' : '#D8D7D2',
                                  borderRadius: 6, background: isPicked ? '#1a1a1a' : '#fff',
                                  color: isPicked ? '#fff' : '#333', cursor: 'pointer',
                                }}>
                                Use "{v.name}"
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <button onClick={() => setAddNewForVendor(row.origName)}
                        style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, border: '1px solid',
                          borderColor: res && res.action === 'new' ? '#1a1a1a' : '#D8D7D2',
                          borderRadius: 6, background: res && res.action === 'new' ? '#1a1a1a' : '#fff',
                          color: res && res.action === 'new' ? '#fff' : '#1A5C38', cursor: 'pointer' }}>
                        + Add "{row.origName}" as new vendor
                      </button>
                      <button onClick={() => setVendorResolutions(prev => ({ ...prev, [row.origName]: { action: 'blank' } }))}
                        style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, border: '1px solid',
                          borderColor: res && res.action === 'blank' ? '#1a1a1a' : '#D8D7D2',
                          borderRadius: 6, background: res && res.action === 'blank' ? '#1a1a1a' : '#fff',
                          color: res && res.action === 'blank' ? '#fff' : '#BF6800', cursor: 'pointer' }}>
                        Import with blank vendor
                      </button>
                      <button onClick={() => setVendorResolutions(prev => ({ ...prev, [row.origName]: { action: 'skip' } }))}
                        style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, border: '1px solid',
                          borderColor: res && res.action === 'skip' ? '#C62828' : '#D8D7D2',
                          borderRadius: 6, background: res && res.action === 'skip' ? '#C62828' : '#fff',
                          color: res && res.action === 'skip' ? '#fff' : '#C62828', cursor: 'pointer' }}>
                        Skip these {row.count} item{row.count === 1 ? '' : 's'}
                      </button>
                      {res && (
                        <button onClick={() => setVendorResolutions(prev => { const n = { ...prev }; delete n[row.origName]; return n; })}
                          style={{ padding: '6px 10px', fontSize: 11, border: 'none',
                            borderRadius: 6, background: 'none', color: '#888', cursor: 'pointer' }}>
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {skippedItemCount > 0 && (
          <div style={{ background: '#FFEBEE', border: '1px solid #ef9a9a', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#C62828' }}>
            {skippedItemCount} item{skippedItemCount === 1 ? '' : 's'} will be skipped because their vendor was not resolved.
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button onClick={() => { runDuplicateCheckAndCommit(buildResolvedImportSet()); setVendorResolveStep(false); }}
            disabled={!allVendorsResolved}
            data-testid="vendor-resolve-continue"
            style={{ padding: '10px 24px', background: allVendorsResolved ? '#1a1a1a' : '#e0e0e0',
              color: allVendorsResolved ? '#fff' : '#aaa', border: 'none', borderRadius: 6,
              fontSize: 14, fontWeight: 600, cursor: allVendorsResolved ? 'pointer' : 'not-allowed' }}>
            Continue import →
          </button>
          <button onClick={() => setVendorResolveStep(false)}
            style={{ padding: '10px 16px', background: 'none', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#888' }}>
            ← Back to review
          </button>
          <div style={{ fontSize: 12, color: '#888' }}>
            {unresolvedCount > 0
              ? unresolvedCount + ' vendor' + (unresolvedCount === 1 ? '' : 's') + ' still need' + (unresolvedCount === 1 ? 's' : '') + ' resolution'
              : 'All vendors resolved'}
          </div>
        </div>

        {addNewForVendor !== null && (
          <DuplicateCheckModal
            vendors={vendors || []}
            items={existingItems || []}
            purchaseOrders={[]}
            initialName={addNewForVendor}
            onPick={(name) => {
              // The dup-check modal handles both "pick existing" and
              // "add new" — both surface here as a final canonical name
              // to use for the imported items. If the modal added a new
              // vendor, onAddVendor has already been awaited inside.
              const vendorList = vendors || [];
              const isExisting = vendorList.some(v => v && v.name === name);
              setVendorResolutions(prev => ({
                ...prev,
                [addNewForVendor]: { action: isExisting ? 'matched' : 'new', resolvedName: name },
              }));
            }}
            onAddVendor={onAddVendor}
            onClose={() => setAddNewForVendor(null)}
          />
        )}
      </div>
    );
  }

  // ── DUPLICATE REVIEW ────────────────────────────────────────────────────
  if (dupStep && duplicates.length > 0) {
    const fmt = n => n ? '$' + Number(n).toLocaleString('en-US', {minimumFractionDigits:2}) : '—';
    return (
      <div style={{ maxWidth: 900 }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
            {duplicates.length} duplicate{duplicates.length > 1 ? 's' : ''} found
          </div>
          <div style={{ fontSize: 13, color: '#666' }}>
            These items already exist in your database. Choose what to do with each one.
          </div>
        </div>

        {/* Bulk actions */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button onClick={() => setDuplicates(prev => prev.map(d => ({...d, choice: 'skip'})))}
            style={{ padding: '5px 14px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer', background: '#f5f5f5' }}>
            Skip all
          </button>
          <button onClick={() => setDuplicates(prev => prev.map(d => ({...d, choice: 'replace'})))}
            style={{ padding: '5px 14px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer', background: '#fff3e0', color: '#e65100', fontWeight: 600 }}>
            Replace all
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
          {duplicates.map((d, i) => (
            <div key={i} style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
              {/* Header */}
              <div style={{ background: '#f5f5f5', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{d.incoming.item}</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['skip', 'replace', 'add'].map(choice => (
                    <button key={choice} onClick={() => setDuplicates(prev => prev.map((x, j) => j === i ? {...x, choice} : x))}
                      style={{ padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid',
                        borderColor: d.choice === choice ? '#1a1a1a' : '#ddd',
                        background: d.choice === choice ? '#1a1a1a' : '#fff',
                        color: d.choice === choice ? '#fff' : '#555' }}>
                      {choice === 'skip' ? 'Skip' : choice === 'replace' ? 'Replace existing' : 'Add as new'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Side by side */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                {[
                  { label: 'EXISTING IN DATABASE', item: d.existing, bg: '#fff' },
                  { label: 'INCOMING FROM FILE', item: d.incoming, bg: d.choice === 'replace' ? '#fff8e1' : '#fafafa' },
                ].map((side, si) => (
                  <div key={si} style={{ padding: '12px 14px', background: side.bg, borderLeft: si === 1 ? '1px solid #e0e0e0' : 'none' }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: '#aaa', letterSpacing: 0.5, marginBottom: 8 }}>{side.label}</div>
                    {[
                      ['Room', (side.item.roomNumber ? side.item.roomNumber + ' ' : '') + (side.item.roomName || '—')],
                      ['Schedule', side.item.schedule || side.item.category || '—'],
                      ['Vendor', side.item.vendor || '—'],
                      ['Status', side.item.status || '—'],
                      ['Net cost', fmt(side.item.netPrice || side.item.unitCost)],
                      ['Dimensions', side.item.specs?.dimensions || '—'],
                      ['Finish', side.item.specs?.finish || '—'],
                    ].map(([label, val]) => (
                      <div key={label} style={{ display: 'flex', gap: 8, marginBottom: 4, fontSize: 11 }}>
                        <span style={{ color: '#aaa', width: 70, flexShrink: 0 }}>{label}</span>
                        <span style={{ color: '#333', fontWeight: val === '—' ? 400 : 500 }}>{val}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={commitDuplicates}
            style={{ padding: '10px 24px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Confirm {duplicates.filter(d => d.choice !== 'skip').length} changes →
          </button>
          <button onClick={() => { setDupStep(false); setDone(true); }}
            style={{ padding: '10px 14px', background: 'none', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#888' }}>
            Skip all & finish
          </button>
          <button onClick={reset}
            style={{ padding: '10px 14px', background: 'none', border: '1px solid #ffcdd2', borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#c62828' }}>
            Cancel import
          </button>
        </div>
      </div>
    );
  }

  // ── DONE ────────────────────────────────────────────────────────────────
  if (done) return (
    <div style={{ textAlign: 'center', padding: '48px 24px' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
        {docType === 'vendors'
          ? `${vendorPreview?.length} vendors imported to your directory`
          : `${selectedCount} items imported to ${projectLabel}`}
      </div>
      <div style={{ fontSize: 13, color: '#888', marginBottom: 24 }}>
        {docType === 'vendors'
          ? 'They are now available in the Vendors tab'
          : 'They are now in your Items database tagged as "Import Wizard"'}
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button onClick={reset} style={{ padding: '9px 20px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          Import Another File
        </button>
        <button onClick={reset} style={{ padding: '9px 20px', background: 'none', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, color: '#555', cursor: 'pointer' }}>
          ← Back to Import Setup
        </button>
      </div>
    </div>
  );

  // ── VENDOR REVIEW ────────────────────────────────────────────────────────
  if (step === 3 && vendorPreview) return (
    <div style={{ maxWidth: 860 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Review Vendors Before Importing</div>
        <div style={{ fontSize: 13, color: '#666' }}>
          Found <strong>{vendorPreview.length} vendors</strong> — duplicates already removed. Review below, then confirm.
        </div>
      </div>

      <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden', marginBottom: 16, maxHeight: 400, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f5f5f5', position: 'sticky', top: 0 }}>
              {['Vendor', 'City', 'Contact', 'Email', 'Phone', 'Notes'].map(h => (
                <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #e0e0e0' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {vendorPreview.map((v, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td style={{ padding: '7px 10px', fontWeight: 600 }}>{v.name}</td>
                <td style={{ padding: '7px 10px', color: '#555' }}>{v.city || '—'}</td>
                <td style={{ padding: '7px 10px', color: '#555' }}>{v.contact || '—'}</td>
                <td style={{ padding: '7px 10px', color: '#1a56db', fontSize: 11 }}>{v.email || '—'}</td>
                <td style={{ padding: '7px 10px', color: '#555' }}>{v.phone || '—'}</td>
                <td style={{ padding: '7px 10px', color: '#777', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.notes || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 11, color: '#888', marginBottom: 16 }}>
        Vendors already in your directory with the same name will be skipped — no duplicates created.
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={() => {
          onImportVendors && onImportVendors(vendorPreview);
          !onImportVendors && console.error('onImportVendors prop not passed to ImportWizard');
          setDone(true);
        }} style={{ padding: '10px 28px', background: onImportVendors ? '#2e7d32' : '#aaa', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          Import {vendorPreview.length} Vendors →
        </button>
        <button onClick={() => { setStep(1); setVendorPreview(null); }}
          style={{ padding: '10px 16px', background: 'none', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#888' }}>
          Back
        </button>
      </div>
    </div>
  );

  // ── STEP 1: SETUP ───────────────────────────────────────────────────────
  if (step === 1) return (
    <div style={{ maxWidth: 680 }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Import Wizard</div>
        <div style={{ fontSize: 13, color: '#666', lineHeight: 1.6 }}>
          Upload any document — Claude will read it and extract items for review before anything is saved.
        </div>
      </div>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24 }}>
        {['Upload & identify', 'Parse', 'Review & edit', 'Save'].map((s, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', margin: '0 auto 4px',
              background: step > i + 1 ? '#2e7d32' : step === i + 1 ? '#1a1a1a' : '#e0e0e0',
              color: step >= i + 1 ? '#fff' : '#888', fontSize: 11, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</div>
            <div style={{ fontSize: 10, color: step === i + 1 ? '#1a1a1a' : '#aaa' }}>{s}</div>
          </div>
        ))}
      </div>

      {/* File drop zone */}
      <div onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
        onClick={() => !file && fileRef.current?.click()}
        style={{ border: `2px dashed ${dragOver ? '#1a1a1a' : file ? '#2e7d32' : '#ddd'}`,
          borderRadius: 10, padding: '24px', textAlign: 'center',
          background: file ? '#e8f5e9' : dragOver ? '#f5f5f3' : '#fafafa',
          cursor: file ? 'default' : 'pointer', marginBottom: 20, transition: 'all .15s' }}>
        {file ? (
          <div>
            <div style={{ fontSize: 28, marginBottom: 6 }}>
              {file.name.endsWith('.csv') ? '📊' : file.name.endsWith('.pdf') ? '📄' : file.name.match(/\.(xlsx|xls)$/) ? '📊' : '📎'}
            </div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#2e7d32', marginBottom: 2 }}>{file.name}</div>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>
              {(file.size / 1024).toFixed(0)}KB · Ready to parse
            </div>
            {file.size > 50 * 1024 && (
              <div style={{ fontSize: 10, color: '#e65100', background: '#fff8e1', padding: '4px 8px', borderRadius: 4, marginBottom: 4 }}>
                Large file — Claude will parse as much as possible. If items are missing, try splitting into smaller sections.
              </div>
            )}
            <button onClick={e => { e.stopPropagation(); setFile(null); setDocType(''); }}
              style={{ fontSize: 11, color: '#888', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
              Remove
            </button>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 36, marginBottom: 8 }}>📂</div>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#333', marginBottom: 4 }}>Drop any file here or click to browse</div>
            <div style={{ fontSize: 11, color: '#aaa' }}>CSV · Excel · PDF · Word · Images of schedules</div>
          </div>
        )}
        <input ref={fileRef} type="file" onChange={e => handleFile(e.target.files[0])} style={{ display: 'none' }}
          accept=".csv,.xlsx,.xls,.pdf,.doc,.docx,.txt,image/*" />
      </div>

      {file && (
        <div>
          {/* Document type */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#555', letterSpacing: 0.3, marginBottom: 8 }}>
              WHAT TYPE OF DOCUMENT IS THIS?
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {DOC_TYPES.map(dt => (
                <button key={dt.value} onClick={() => setDocType(dt.value)} style={{
                  padding: '10px 14px', border: docType === dt.value ? '2px solid #1a1a1a' : '1px solid #e0e0e0',
                  borderRadius: 8, background: docType === dt.value ? '#1a1a1a' : '#fff',
                  color: docType === dt.value ? '#fff' : '#333', cursor: 'pointer', textAlign: 'left',
                }}>
                  <div style={{ fontWeight: 600, fontSize: 12 }}>{dt.label}</div>
                  <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2 }}>{dt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Project — not needed for vendor lists */}
          {docType !== 'vendors' && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#555', letterSpacing: 0.3, marginBottom: 8 }}>
              WHICH PROJECT?
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {(projects || []).map(p => (
                <button key={p.id} onClick={() => setProjectLabel(p.label)} style={{
                  padding: '7px 16px', border: projectLabel === p.label ? '2px solid #1a1a1a' : '1px solid #ddd',
                  borderRadius: 20, background: projectLabel === p.label ? '#1a1a1a' : '#fff',
                  color: projectLabel === p.label ? '#fff' : '#555', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                }}>{p.label}</button>
              ))}
            </div>
          </div>
          )}

          {/* Schedule override — not needed for vendor lists */}
          {docType !== 'vendors' && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#555', letterSpacing: 0.3, marginBottom: 8 }}>
              FORCE ALL ITEMS TO THIS SCHEDULE? <span style={{ fontWeight: 400, color: '#aaa' }}>(optional — leave blank to auto-detect)</span>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button onClick={() => setForceSchedule('')} style={{
                padding: '6px 14px', border: forceSchedule === '' ? '2px solid #1a1a1a' : '1px solid #ddd',
                borderRadius: 20, background: forceSchedule === '' ? '#1a1a1a' : '#fff',
                color: forceSchedule === '' ? '#fff' : '#555', fontSize: 12, cursor: 'pointer', fontWeight: 600,
              }}>Auto-detect</button>
              {['Furniture','Lighting','Cushions & Throw Pillows','Art & Objects','Hardware','Appliances','Tile & Stone','Paint & Wallpaper','Finish Schedule','Window Treatments','Softgoods','Millwork'].map(s => (
                <button key={s} onClick={() => setForceSchedule(s)} style={{
                  padding: '6px 14px', border: forceSchedule === s ? '2px solid #1a1a1a' : '1px solid #ddd',
                  borderRadius: 20, background: forceSchedule === s ? '#1a1a1a' : '#fff',
                  color: forceSchedule === s ? '#fff' : '#555', fontSize: 12, cursor: 'pointer',
                }}>{s}</button>
              ))}
            </div>
            {forceSchedule && <div style={{ fontSize: 11, color: '#2e7d32', marginTop: 6, fontWeight: 600 }}>
              ✓ All items will be assigned to: {forceSchedule}
            </div>}
          </div>
          )}

          {/* Pre-App toggle — not needed for vendor lists */}
          {docType !== 'vendors' && (
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
              <div onClick={() => setIsPreApp(p => !p)} style={{
                width: 40, height: 22, borderRadius: 11, position: 'relative',
                background: isPreApp ? '#4527a0' : '#ddd', transition: 'background .2s', flexShrink: 0,
              }}>
                <div style={{
                  position: 'absolute', top: 2, left: isPreApp ? 20 : 2,
                  width: 18, height: 18, borderRadius: '50%', background: '#fff',
                  transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: isPreApp ? '#4527a0' : '#333' }}>
                  {isPreApp ? 'ABD (Andra Birkerts Design)' : 'Current document'}
                </div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>
                  {isPreApp
                    ? 'Items purchased through ABD — tagged for reference, excluded from financials'
                    : 'Items are being actively tracked in this system — no special tag'}
                </div>
              </div>
            </label>
          </div>
          )}

          {(() => {
            const ready = docType === 'vendors' ? !!docType : (!!docType && !!projectLabel);
            return (
              <button onClick={startParsing} disabled={!ready}
                style={{ padding: '10px 28px', background: ready ? '#1a1a1a' : '#e0e0e0',
                  color: ready ? '#fff' : '#aaa', border: 'none', borderRadius: 6,
                  fontSize: 14, fontWeight: 600, cursor: ready ? 'pointer' : 'not-allowed' }}>
                {docType === 'vendors' ? 'Import Vendors →' : 'Parse Document →'}
              </button>
            );
          })()}
        </div>
      )}
    </div>
  );

  // ── STEP 2: PARSING ─────────────────────────────────────────────────────
  if (step === 2) return (
    <div style={{ textAlign: 'center', padding: '48px 24px' }}>
      <div style={{ fontSize: 36, marginBottom: 16 }}>🔍</div>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Reading {file?.name}...</div>
      <div style={{ fontSize: 13, color: '#888', marginBottom: 24 }}>{parseStage}</div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 24 }}>
        {[0,1,2].map(i => (
          <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: '#1a1a1a',
            animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite` }} />
        ))}
      </div>
      <button onClick={reset} style={{ padding: '7px 18px', background: 'none', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#888' }}>
        Cancel
      </button>
      <style>{`@keyframes bounce { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-6px)} }`}</style>
    </div>
  );

  // ── STEP 3: REVIEW ──────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: 1100 }}>
      {items.length === 1 && items[0]._error && (
        <div style={{ background: '#ffebee', border: '1px solid #ef9a9a', borderRadius: 10, padding: '20px 24px', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#c62828', marginBottom: 8 }}>Parsing failed</div>
          <div style={{ fontSize: 13, color: '#333', marginBottom: 16, lineHeight: 1.6 }}>{items[0].errorMessage}</div>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 16, lineHeight: 1.6 }}>
            <strong>Things to try:</strong><br/>
            • If it's an Excel file (.xlsx), export it as CSV first from Google Sheets or Excel<br/>
            • If it's a scanned PDF, the text may not be machine-readable — try a text-based PDF<br/>
            • Make sure the document actually contains a list of items (not just a cover page)<br/>
            • Try selecting "Other Document" as the type so Claude figures it out
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={reset} style={{ padding: '8px 20px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Try Again
            </button>
            <button onClick={reset} style={{ padding: '8px 16px', background: 'none', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#888' }}>
              ← Start Over
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 2 }}>Review extracted items</div>
          <div style={{ fontSize: 12, color: '#888' }}>
            {file?.name} · {projectLabel} · {selectedCount} of {totalCount} selected
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => selectAll(true)} style={{ padding: '5px 12px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 5, fontSize: 11, cursor: 'pointer' }}>Select all</button>
          <button onClick={() => selectAll(false)} style={{ padding: '5px 12px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 5, fontSize: 11, cursor: 'pointer' }}>Deselect all</button>
          <button onClick={() => setStep(1)} style={{ padding: '5px 12px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 5, fontSize: 11, cursor: 'pointer', color: '#555' }}>← Back to setup</button>
          <button onClick={reset} style={{ padding: '5px 12px', background: '#fff0f0', border: '1px solid #ffcdd2', borderRadius: 5, fontSize: 11, cursor: 'pointer', color: '#c62828' }}>✕ Cancel</button>
        </div>
      </div>

      {/* Project assignment banner */}
      <div style={{ background: '#e8f0fe', border: '1px solid #90caf9', borderRadius: 8, padding: '10px 16px',
        marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
        <span style={{ fontWeight: 600 }}>Project:</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {(projects || []).map(p => (
            <button key={p.id} onClick={() => setItems(prev => prev.map(it => ({ ...it, project: p.label, projectLabel: p.label })))} style={{
              padding: '3px 10px', border: items[0]?.projectLabel === p.label ? '2px solid #1a56db' : '1px solid #90caf9',
              borderRadius: 12, background: items[0]?.projectLabel === p.label ? '#1a56db' : '#fff',
              color: items[0]?.projectLabel === p.label ? '#fff' : '#1a56db', fontSize: 11, fontWeight: 600, cursor: 'pointer',
            }}>{p.label}</button>
          ))}
        </div>
        <span style={{ fontSize: 11, color: '#888' }}>Click to reassign all items</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          {isPreApp
            ? <span style={{ background: '#ede7f6', color: '#4527a0', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10 }}>ABD tag will be applied</span>
            : <span style={{ background: '#f5f5f5', color: '#888', fontSize: 11, padding: '3px 10px', borderRadius: 10 }}>No special tag</span>}
        </div>
      </div>

      {/* Items table */}
      <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ background: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
              {['', 'Item', 'Category', 'Vendor', 'Room', 'Qty', 'Dimensions', 'Finish', 'Net Cost', 'Status', 'Confidence', ''].map((h, i) => (
                <th key={i} style={{ padding: '8px 8px', textAlign: 'left', fontWeight: 600, color: '#555', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => {
              const isEditing = editingIdx === idx;
              const conf = it.confidence || 'medium';
              const confColor = conf === 'high' ? '#2e7d32' : conf === 'medium' ? '#e65100' : '#c62828';
              const isSelected = selected[idx] !== false;

              return (
                <tr key={idx} style={{ borderBottom: '1px solid #f5f5f5',
                  background: !isSelected ? '#fafafa' : isEditing ? '#fffde7' : idx % 2 === 0 ? '#fff' : '#fafafa',
                  opacity: isSelected ? 1 : 0.5 }}>

                  {/* Select */}
                  <td style={{ padding: '6px 8px', width: 32 }}>
                    <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(idx)} />
                  </td>

                  {/* Item name */}
                  <td style={{ padding: '6px 8px', minWidth: 140, maxWidth: 200 }}>
                    {isEditing
                      ? <input value={it.item} onChange={e => updateItem(idx, 'item', e.target.value)} style={inp} />
                      : <div style={{ fontWeight: 600, fontSize: 11 }}>{it.item || '—'}</div>}
                    {it.specs?.manufacturer && !isEditing && <div style={{ fontSize: 9, color: '#888' }}>{it.specs.manufacturer}</div>}
                  </td>

                  {/* Category */}
                  <td style={{ padding: '6px 8px', width: 100 }}>
                    {isEditing
                      ? <select value={it.category} onChange={e => { updateItem(idx, 'category', e.target.value); updateItem(idx, 'schedule', e.target.value); }} style={inp}>
                          {SCHEDULES.map(c => <option key={c}>{c}</option>)}
                        </select>
                      : <span style={{ fontSize: 10, background: '#e8f0fe', color: '#1a56db', padding: '1px 6px', borderRadius: 8, fontWeight: 600 }}>{it.category}</span>}
                  </td>

                  {/* Vendor */}
                  <td style={{ padding: '6px 8px', minWidth: 100 }}>
                    {isEditing
                      ? <input value={it.vendor} onChange={e => updateItem(idx, 'vendor', e.target.value)} style={inp} />
                      : it.vendor || '—'}
                  </td>

                  {/* Room */}
                  <td style={{ padding: '6px 8px', minWidth: 80 }}>
                    {isEditing
                      ? <div style={{ display: 'flex', gap: 4 }}>
                          <input value={it.roomNumber} onChange={e => updateItem(idx, 'roomNumber', e.target.value)}
                            style={{ ...inp, width: 36 }} placeholder="#" />
                          <input value={it.roomName} onChange={e => updateItem(idx, 'roomName', e.target.value)}
                            style={inp} placeholder="Room" />
                        </div>
                      : <span>{it.roomNumber ? it.roomNumber + ' ' : ''}{it.roomName || '—'}</span>}
                  </td>

                  {/* Qty */}
                  <td style={{ padding: '6px 8px', width: 60 }}>
                    {isEditing
                      ? <input value={it.qty} onChange={e => updateItem(idx, 'qty', e.target.value)} style={inp} />
                      : `${it.qty || 1} ${it.qtyType || ''}`}
                  </td>

                  {/* Dimensions */}
                  <td style={{ padding: '6px 8px', minWidth: 100 }}>
                    {isEditing
                      ? <input value={it.specs?.dimensions || ''} onChange={e => updateSpec(idx, 'dimensions', e.target.value)} style={inp} />
                      : it.specs?.dimensions || '—'}
                  </td>

                  {/* Finish */}
                  <td style={{ padding: '6px 8px', minWidth: 80 }}>
                    {isEditing
                      ? <input value={it.specs?.finish || ''} onChange={e => updateSpec(idx, 'finish', e.target.value)} style={inp} />
                      : it.specs?.finish || '—'}
                  </td>

                  {/* Net Cost */}
                  <td style={{ padding: '6px 8px', width: 80 }}>
                    {isEditing
                      ? <input value={it.netPrice || ''} onChange={e => updateItem(idx, 'netPrice', e.target.value)} style={inp} placeholder="0.00" />
                      : it.netPrice ? `$${parseFloat(it.netPrice).toLocaleString()}` : '—'}
                  </td>

                  {/* Status */}
                  <td style={{ padding: '6px 8px', minWidth: 100 }}>
                    {isEditing
                      ? <select value={it.status} onChange={e => updateItem(idx, 'status', e.target.value)} style={inp}>
                          {STATUSES.map(s => <option key={s}>{s}</option>)}
                        </select>
                      : <span style={{ fontSize: 9, fontWeight: 600, color: '#555' }}>{it.status}</span>}
                  </td>

                  {/* Confidence */}
                  <td style={{ padding: '6px 8px', width: 60 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, color: confColor }}>{conf.toUpperCase()}</span>
                  </td>

                  {/* Actions */}
                  <td style={{ padding: '6px 8px', width: 80 }}>
                    <div style={{ display: 'flex', gap: 3 }}>
                      {isEditing
                        ? <button onClick={() => setEditingIdx(null)} style={{ padding: '2px 8px', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 3, fontSize: 9, fontWeight: 700, cursor: 'pointer' }}>Done</button>
                        : <button onClick={() => setEditingIdx(idx)} style={{ padding: '2px 8px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 3, fontSize: 9, cursor: 'pointer' }}>Edit</button>}
                      <button onClick={() => removeItem(idx)} style={{ padding: '2px 6px', background: '#ffebee', border: 'none', borderRadius: 3, fontSize: 9, cursor: 'pointer', color: '#c62828' }}>✕</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, fontSize: 10, color: '#888' }}>
        <span><strong style={{ color: '#2e7d32' }}>HIGH</strong> — extracted with certainty</span>
        <span><strong style={{ color: '#e65100' }}>MEDIUM</strong> — extracted but verify</span>
        <span><strong style={{ color: '#c62828' }}>LOW</strong> — guessed, needs review</span>
      </div>

      
      {/* Commit */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button onClick={commitImport} disabled={selectedCount === 0}
          style={{ padding: '10px 28px', background: selectedCount > 0 ? '#1a1a1a' : '#e0e0e0',
            color: selectedCount > 0 ? '#fff' : '#aaa', border: 'none', borderRadius: 6,
            fontSize: 14, fontWeight: 600, cursor: selectedCount > 0 ? 'pointer' : 'not-allowed' }}>
          Import {selectedCount} Items to {projectLabel} →
        </button>
        <div style={{ fontSize: 12, color: '#888' }}>
          Unselected items will be skipped
        </div>
      </div>
    </div>
  );
}
